/**
 * Code editor panel for Mermaid syntax
 * Provides syntax highlighting and bidirectional sync with visual editor
 */

import { useCallback, useEffect, useId, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { useTheme } from "@/app/providers/theme-provider"
import { AlertCircle, Check, Eye, EyeOff, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { useMermaidEditorStore } from "../stores/mermaid-editor-store"
import { renderSanitizedMermaidSvg } from "../utils/mermaid-safe-render"

type MermaidCodeEditorProps = {
  className?: string
  readOnly?: boolean
  showPreview?: boolean
}

export function MermaidCodeEditor({
  className,
  readOnly = false,
  showPreview: initialShowPreview = true,
}: MermaidCodeEditorProps) {
  const { t } = useTranslation()
  const { resolvedTheme: theme } = useTheme()
  const uniqueId = useId()
  const previewRef = useRef<HTMLDivElement>(null)

  const { code, setCode, isValid, parseError, syncVisualFromCode } = useMermaidEditorStore()

  const [localCode, setLocalCode] = useState(code)
  const [showPreview, setShowPreview] = useState(initialShowPreview)
  const [previewSvg, setPreviewSvg] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [isRendering, setIsRendering] = useState(false)

  const currentTheme = theme === "dark" ? "dark" : "light"
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setLocalCode(code)
  }, [code])

  const renderPreview = useCallback(async () => {
    if (!showPreview || !localCode.trim()) {
      setPreviewSvg(null)
      setPreviewError(null)
      return
    }

    setIsRendering(true)
    setPreviewError(null)

    try {
      const cleanId = `mermaid-preview-${uniqueId.replace(/:/g, "")}-${Date.now()}`
      const svg = await renderSanitizedMermaidSvg(cleanId, localCode, {
        theme: currentTheme === "dark" ? "dark" : "default",
        fontFamily: "inherit",
      })
      setPreviewSvg(svg)
      setPreviewError(null)
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : "Failed to render diagram")
      setPreviewSvg(null)
    } finally {
      setIsRendering(false)
    }
  }, [localCode, showPreview, currentTheme, uniqueId])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      void renderPreview()
    }, 500)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [renderPreview])

  const handleCodeChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newCode = event.target.value
      setLocalCode(newCode)
      setCode(newCode)
    },
    [setCode]
  )

  const handleSync = useCallback(() => {
    syncVisualFromCode()
  }, [syncVisualFromCode])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Tab") {
        event.preventDefault()
        const textarea = event.currentTarget
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const newValue = localCode.substring(0, start) + "    " + localCode.substring(end)
        setLocalCode(newValue)
        setCode(newValue)

        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 4
        })
      }
    },
    [localCode, setCode]
  )

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Mermaid Code</span>
          {isValid ? (
            <span className="text-success flex items-center gap-1 text-xs">
              <Check className="size-3" />
              Valid
            </span>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-destructive flex cursor-help items-center gap-1 text-xs">
                  <AlertCircle className="size-3" />
                  Invalid
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px]">
                <p className="text-xs">{parseError || "Invalid Mermaid syntax"}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-7"
                size="icon"
                variant="ghost"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {showPreview
                ? t("mermaid.hide_preview", "Hide preview")
                : t("mermaid.show_preview", "Show preview")}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-7"
                disabled={readOnly}
                size="icon"
                variant="ghost"
                onClick={handleSync}
              >
                <RefreshCw className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("mermaid.sync_visual", "Sync to visual editor")}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-3">
        <Textarea
          className="min-h-[200px] flex-1 resize-none font-mono text-sm"
          disabled={readOnly}
          placeholder="flowchart TD&#10;    A[Start] --> B[End]"
          value={localCode}
          onChange={handleCodeChange}
          onKeyDown={handleKeyDown}
        />

        {showPreview && (
          <div className="bg-muted/30 flex-1 overflow-auto rounded-md border p-4">
            {isRendering && (
              <div className="flex h-full items-center justify-center">
                <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
              </div>
            )}
            {previewError && !isRendering && (
              <div className="bg-destructive/10 text-destructive rounded-md p-4 text-sm">
                <p className="font-medium">Preview error</p>
                <pre className="mt-2 text-xs whitespace-pre-wrap opacity-75">{previewError}</pre>
              </div>
            )}
            {previewSvg && !isRendering && (
              <div
                ref={previewRef}
                className="flex justify-center [&_svg]:max-w-full"
                dangerouslySetInnerHTML={{ __html: previewSvg }}
              />
            )}
            {!previewSvg && !previewError && !isRendering && (
              <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                {t("mermaid.no_preview", "Enter Mermaid code to see preview")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
